import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import { Database } from "@/lib/database.types";
import { createAuthHeaders } from "@/lib/auth-utils";

type Plan = Database["public"]["Tables"]["plans"]["Row"];
type Subscription = Database["public"]["Tables"]["subscriptions"]["Row"];
type PlanFeature = Database["public"]["Tables"]["plan_features"]["Row"];
type AIAgent = Database["public"]["Tables"]["ai_agents"]["Row"];

export interface AgentUsageInfo {
  agent: AIAgent;
  dailyUsed: number;
  dailyLimit: number;
  monthlyLimit: number;
  usagePercentage: number;
}

export interface RecentActivity {
  id: string;
  feature_id: string;
  used_at: string;
  metadata: Record<string, unknown>;
}

export interface CreditBalanceInfo {
  total_balance: number;
  plan_credits_remaining: number;
  topup_credits_remaining: number;
}

export interface StorageUsageInfo {
  total_mb: number;
  total_gb: number;
  quota_gb: number;
  percentage: number;
}

export interface SubscriptionUsageData {
  // Subscription & Plan
  subscription: Subscription | null;
  plan: Plan | null;
  planFeatures: PlanFeature[];

  // Credits
  userCredits: CreditBalanceInfo;
  monthlyCreditsLimit: number;
  creditsUsedThisMonth: number;

  // Storage
  storageUsage: StorageUsageInfo;

  // Agents
  planAgents: AIAgent[];
  agentUsage: AgentUsageInfo[];

  // Aggregated KPIs
  daysRemaining: number;
  documentsUsedThisMonth: number;
  procesosAnalizadosThisMonth: number;
  procesosMetrics: {
    upcomingDeadlines: number;
    stalledProcesses: number;
    activeProcesses: number;
    finishedProcesses: number;
  };

  // Despachos quotas
  currentCases: number;
  maxCases: number;
  currentClients: number;
  maxClients: number;
  teamMembers: number;
  maxTeamMembers: number;

  // Activity
  recentActivity: RecentActivity[];

  // Meta
  loading: boolean;
  error: string | null;
}

export function useSubscriptionUsage(): SubscriptionUsageData {
  const { user } = useAuth();
  const [data, setData] = useState<SubscriptionUsageData>({
    subscription: null,
    plan: null,
    planFeatures: [],
    userCredits: { total_balance: 120, plan_credits_remaining: 120, topup_credits_remaining: 0 },
    monthlyCreditsLimit: 120,
    creditsUsedThisMonth: 0,
    storageUsage: { total_mb: 0, total_gb: 0, quota_gb: 5.0, percentage: 0 },
    planAgents: [],
    agentUsage: [],
    daysRemaining: 0,
    documentsUsedThisMonth: 0,
    procesosAnalizadosThisMonth: 0,
    procesosMetrics: {
      upcomingDeadlines: 0,
      stalledProcesses: 0,
      activeProcesses: 0,
      finishedProcesses: 0,
    },
    currentCases: 0,
    maxCases: 0,
    currentClients: 0,
    maxClients: 0,
    teamMembers: 0,
    maxTeamMembers: 0,
    recentActivity: [],
    loading: true,
    error: null,
  });

  const fetchAll = useCallback(async () => {
    if (!user) {
      setData((prev) => ({ ...prev, loading: false }));
      return;
    }

    try {
      setData((prev) => ({ ...prev, loading: true, error: null }));

      // 1. Obtener la suscripción activa
      const { data: subs, error: subErr } = await supabase
        .from("subscriptions")
        .select("*")
        .eq("user_id", user.id)
        .eq("status", "active")
        .order("created_at", { ascending: false })
        .limit(1);

      if (subErr) throw subErr;
      const subscription = subs?.[0] || null;

      if (!subscription) {
        setData((prev) => ({
          ...prev,
          subscription: null,
          plan: null,
          loading: false,
        }));
        return;
      }

      const startOfMonth = new Date();
      startOfMonth.setDate(1);
      startOfMonth.setHours(0, 0, 0, 0);

      const oneDayAgo = new Date();
      oneDayAgo.setDate(oneDayAgo.getDate() - 1);

      // Helper para la llamada de almacenamiento con timeout rápido de 1.5s
      const fetchStorageUsage = async (): Promise<StorageUsageInfo> => {
        try {
          const controller = new AbortController();
          const timer = setTimeout(() => controller.abort(), 1500);
          const authHeaders = await createAuthHeaders();
          const backendUrl = import.meta.env.VITE_BACKEND_URL || "http://localhost:8001";
          const res = await fetch(`${backendUrl}/api/v1/user/storage-usage`, {
            headers: {
              "Content-Type": "application/json",
              ...authHeaders,
            },
            signal: controller.signal,
          });
          clearTimeout(timer);
          if (res.ok) {
            const storageData = await res.json();
            return {
              total_mb: storageData.total_mb || 0,
              total_gb: storageData.total_gb || 0,
              quota_gb: storageData.quota_gb || 5.0,
              percentage: storageData.percentage || 0,
            };
          }
        } catch (e) {
          console.warn("Límite de tiempo o error consultando almacenamiento:", e);
        }
        return { total_mb: 0, total_gb: 0, quota_gb: 5.0, percentage: 0 };
      };

      // Helper para consultar agentes del plan
      const fetchPlanAgents = async (planId: string): Promise<AIAgent[]> => {
        const { data: planAgentLinks } = await supabase
          .from("plan_agents")
          .select("agent_id")
          .eq("plan_id", planId);

        if (planAgentLinks && planAgentLinks.length > 0) {
          const agentIds = planAgentLinks.map((pa) => pa.agent_id);
          const { data: agents } = await supabase
            .from("ai_agents")
            .select("*")
            .in("id", agentIds)
            .eq("is_visible", true);
          return agents || [];
        }
        return [];
      };

      // 2. Ejecutar TODAS las consultas de forma totalmente PARALELA (Promise.all)
      const [
        planRes,
        featuresRes,
        planAgentsRes,
        userCreditsRes,
        logsThisMonthRes,
        storageUsageRes,
        docsCountRes,
        procCountRes,
        procesosRes,
        tramitesRes,
        recentLogsRes,
      ] = await Promise.all([
        supabase.from("plans").select("*").eq("id", subscription.plan_id).single(),
        supabase.from("plan_features").select("*").eq("plan_id", subscription.plan_id),
        fetchPlanAgents(subscription.plan_id),
        supabase.from("user_credits").select("*").eq("user_id", user.id).maybeSingle(),
        supabase.from("credit_usage_logs").select("credits_deducted").eq("user_id", user.id).gte("created_at", startOfMonth.toISOString()),
        fetchStorageUsage(),
        supabase.from("credit_usage_logs").select("*", { count: "exact", head: true }).eq("user_id", user.id).in("feature_name", ["document_drafting", "generate_memorial", "generate_strategic_draft"]).gte("created_at", startOfMonth.toISOString()),
        supabase.from("credit_usage_logs").select("*", { count: "exact", head: true }).eq("user_id", user.id).in("feature_name", ["process_analysis", "analisis-ia-pro", "process-update"]).gte("created_at", startOfMonth.toISOString()),
        supabase.from("procesos").select("id, estado, watch_status, is_watched, last_change_at, fecha_ultima_actuacion, last_sync_at, created_at, metadata").eq("user_id", user.id),
        supabase.from("despachos_tramites").select("due_date, status").eq("user_id", user.id).in("status", ["pending", "in_progress"]),
        supabase.from("credit_usage_logs").select("id, feature_name, created_at, model_used, credits_deducted").eq("user_id", user.id).gte("created_at", oneDayAgo.toISOString()).order("created_at", { ascending: false }).limit(30),
      ]);

      const plan = planRes.data;
      const features = featuresRes.data || [];
      const planAgents = planAgentsRes;
      const userCreditsData = userCreditsRes.data;
      const logsThisMonth = logsThisMonthRes.data || [];
      const storageUsage = storageUsageRes;
      const docsCount = docsCountRes.count || 0;
      const procCount = procCountRes.count || 0;
      const procesosList = procesosRes.data || [];
      const tramitesData = tramitesRes.data || [];
      const recentActivityLogs = recentLogsRes.data || [];

      // Procesar créditos
      const monthlyCreditsLimit = (plan as any)?.monthly_credits || 120;
      const userCredits: CreditBalanceInfo = {
        total_balance: userCreditsData
          ? (userCreditsData.plan_credits_remaining || 0) + (userCreditsData.topup_credits_remaining || 0)
          : monthlyCreditsLimit,
        plan_credits_remaining: userCreditsData?.plan_credits_remaining ?? monthlyCreditsLimit,
        topup_credits_remaining: userCreditsData?.topup_credits_remaining ?? 0,
      };

      const creditsUsedThisMonth = logsThisMonth.reduce(
        (sum, log) => sum + Math.max(0, log.credits_deducted || 0),
        0
      );

      // Calcular días restantes de ciclo
      let daysRemaining = 0;
      if (subscription.ends_at) {
        const endsAt = new Date(subscription.ends_at);
        const now = new Date();
        daysRemaining = Math.max(
          0,
          Math.ceil((endsAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
        );
      } else if (subscription.is_trial && subscription.trial_ends_at) {
        const trialEnds = new Date(subscription.trial_ends_at);
        const now = new Date();
        daysRemaining = Math.max(
          0,
          Math.ceil((trialEnds.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
        );
      }

      // Métricas de procesos
      const casesCount = procesosList.length;
      const nowTime = new Date().getTime();

      let upcomingDeadlines = 0;
      let stalledProcesses = 0;
      let activeProcesses = 0;
      let finishedProcesses = 0;

      procesosList.forEach((p) => {
        const meta = (p.metadata as any) || {};
        const watchStatus = p.watch_status || (p.is_watched ? "active" : "inactive");
        const estadoStr = String(p.estado || watchStatus || "").toLowerCase();

        if (
          estadoStr.includes("terminado") ||
          estadoStr.includes("archivo") ||
          estadoStr.includes("closed") ||
          estadoStr.includes("finalizado") ||
          watchStatus === "closed" ||
          watchStatus === "archived"
        ) {
          finishedProcesses++;
        } else {
          activeProcesses++;

          const lastDateStr = p.last_change_at || p.fecha_ultima_actuacion || p.last_sync_at || p.created_at;
          if (lastDateStr) {
            const lastUpdate = new Date(lastDateStr).getTime();
            if (!isNaN(lastUpdate)) {
              const daysSinceUpdate = (nowTime - lastUpdate) / (1000 * 60 * 60 * 24);
              if (daysSinceUpdate > 30) {
                stalledProcesses++;
              }
            }
          } else {
            stalledProcesses++;
          }
        }

        const deadlineField = meta.proxima_audiencia || meta.vencimiento_termino || meta.fecha_limite || meta.due_date;
        if (deadlineField) {
          const deadlineDate = new Date(deadlineField).getTime();
          if (!isNaN(deadlineDate)) {
            const daysToDeadline = (deadlineDate - nowTime) / (1000 * 60 * 60 * 24);
            if (daysToDeadline >= -1 && daysToDeadline <= 7) {
              upcomingDeadlines++;
            }
          }
        }
      });

      tramitesData.forEach((t) => {
        if (t.due_date) {
          const due = new Date(t.due_date).getTime();
          if (!isNaN(due)) {
            const diffDays = (due - nowTime) / (1000 * 60 * 60 * 24);
            if (diffDays >= -1 && diffDays <= 7) {
              upcomingDeadlines++;
            }
          }
        }
      });

      const uniqueClients = new Set(
        procesosList
          .map((p) => (p.metadata as any)?.client_email)
          .filter(Boolean)
      );

      const recentActivity: RecentActivity[] = recentActivityLogs.map((log) => ({
        id: log.id,
        feature_id: log.feature_name,
        used_at: log.created_at,
        metadata: {
          model: log.model_used,
          credits: log.credits_deducted,
        },
      }));

      setData({
        subscription,
        plan,
        planFeatures: features,
        userCredits,
        monthlyCreditsLimit,
        creditsUsedThisMonth,
        storageUsage,
        planAgents,
        agentUsage: [],
        daysRemaining,
        documentsUsedThisMonth: docsCount,
        procesosAnalizadosThisMonth: procCount,
        procesosMetrics: {
          upcomingDeadlines,
          stalledProcesses,
          activeProcesses,
          finishedProcesses,
        },
        currentCases: casesCount,
        maxCases: plan?.max_cases || 10,
        currentClients: uniqueClients.size,
        maxClients: plan?.max_clients || 20,
        teamMembers: 1,
        maxTeamMembers: plan?.max_team_members || 1,
        recentActivity,
        loading: false,
        error: null,
      });
    } catch (err: any) {
      console.error("Error loading subscription usage data:", err);
      setData((prev) => ({
        ...prev,
        loading: false,
        error: err.message || "Error al cargar datos de uso",
      }));
    }
  }, [user]);

  useEffect(() => {
    fetchAll();
    const handleUpdate = () => fetchAll();
    window.addEventListener("subscription_updated", handleUpdate);
    window.addEventListener("credits-updated", handleUpdate);
    return () => {
      window.removeEventListener("subscription_updated", handleUpdate);
      window.removeEventListener("credits-updated", handleUpdate);
    };
  }, [fetchAll]);

  return data;
}
